/**
 * Router principale del portale Tenacious C (react-router v6).
 *
 * Struttura:
 *   /login               — pagina di login (pubblica)
 *   /auth/callback       — callback OIDC SSO (pubblica, TD-004)
 *   /auth/entra/callback — callback login Microsoft Entra ID (pubblica, TC_AUTH_PROVIDER=entra)
 *   /                    — dashboard; edizione trial (VITE_EDITION): redirect a /enisa-reports
 *   /dashboard           — dashboard (alias, raggiungibile anche nell'edizione trial)
 *   /products            — lista prodotti (E10.2)
 *   /products/:id        — dettaglio prodotto (E10.2)
 *   /tickets             — Ticket per prodotto + lista globale CVE (righe espandibili → release
 *                          affette) (E10.9 / RF-11)
 *   /tickets/:projectUuid — triage vulnerabilità + export VEX (E10.4/E10.5)
 *   /triage, /triage/*   — redirect storico verso /tickets… (query e hash preservati)
 *   /enisa-reports       — report ENISA: thread di notifica SRP aperti/precedenti + SLA (E10.6/E10.7)
 *   /enisa-reports/:id   — dettaglio report (note/categoria/step/risoluzione, CRA Art.14(2)),
 *                          modificabile anche per i report precedenti (chiusi)
 *   /srp, /srp/*         — redirect storico verso /enisa-reports… (query e hash preservati)
 *   /admin               — configurazione sistema (E10.11 / RF-7-1+RF-12-1 — slice 4)
 *   /jobs                — portale job Prefect (flow-run + log; visibile a tutti i ruoli)
 *   /batch-edits         — batch della Batch Edit API (statistiche, live) + dettaglio /:batchId
 *
 * Tutte le route sotto layout sono avvolte in RequireAuth.
 */

import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Layout } from './Layout.js';
import { isTrialEdition } from './edition.js';
import { RequireAuth } from '../auth/RequireAuth.js';
import { ENTRA_CALLBACK_PATH } from '../auth/entra.js';
import { EntraCallbackPage } from '../pages/EntraCallbackPage.js';
import { DashboardPage } from '../pages/DashboardPage.js';
import { LoginPage } from '../pages/LoginPage.js';
import { AuthCallbackPage } from '../pages/AuthCallbackPage.js';
import { ProductListPage } from '../features/products/ProductListPage.js';
import { ProductDetailPage } from '../features/products/ProductDetailPage.js';
import { TriagePage } from '../features/triage/TriagePage.js';
// Slice 4 — E10.6/E10.7/E10.11
import { ReportPage } from '../features/report/ReportPage.js';
import { SrpThreadDetail } from '../features/srp/SrpThreadDetail.js';
import { LegacySrpRedirect } from './LegacySrpRedirect.js';
import { LegacyTriageRedirect } from './LegacyTriageRedirect.js';
import { AdminPage } from '../features/admin/AdminPage.js';
// Slice A — Anagrafica clienti (Forniture)
import { CustomersListPage } from '../features/customers/CustomersListPage.js';
import { CustomerDetailPage } from '../features/customers/CustomerDetailPage.js';
// Portale job Prefect (visibile a tutti i ruoli autenticati)
import { PrefectJobsPage } from '../features/prefect/index.js';
// Log Audit (E7.6 — VIEW_AUDIT_LOG)
import { AuditLogPage } from '../features/audit/AuditLogPage.js';
// Batch Edit API: batch di modifiche massive al catalogo (VIEW_PORTFOLIO)
import { BatchEditsPage, BatchEditDetailPage } from '../features/batch-edits/index.js';
// Glossario dei termini del dominio CRA (statico)
import { GlossarioPage } from '../features/glossary/index.js';

export const router = createBrowserRouter([
  {
    // Route pubblica: login
    path: '/login',
    element: <LoginPage />,
  },
  {
    // Route pubblica: callback OIDC SSO (TD-004)
    path: '/auth/callback',
    element: <AuthCallbackPage />,
  },
  {
    // Route pubblica: callback login Microsoft Entra ID (oidc-client-ts, TC_AUTH_PROVIDER=entra)
    path: ENTRA_CALLBACK_PATH,
    element: <EntraCallbackPage />,
  },
  {
    // Route protette: avvolte nel layout con nav/header
    path: '/',
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    children: [
      // Edizione trial: la Dashboard non è in nav → il landing post-login è ENISA Reports.
      {
        index: true,
        element: isTrialEdition() ? <Navigate to="/enisa-reports" replace /> : <DashboardPage />,
      },
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      // E10.2 — vista Prodotti (lista + dettaglio con Release/Mapping)
      {
        path: 'products',
        element: <ProductListPage />,
      },
      {
        path: 'products/:id',
        element: <ProductDetailPage />,
      },
      // E10.4/E10.5 — pagina "Tickets": triage vulnerabilità + export VEX (slice 3)
      {
        path: 'tickets',
        element: <TriagePage />,
      },
      {
        path: 'tickets/:projectUuid',
        element: <TriagePage />,
      },
      // Route storiche /triage e /triage/:projectUuid → /tickets… (bookmark, link già inviati)
      {
        path: 'triage',
        element: <LegacyTriageRedirect />,
      },
      {
        path: 'triage/*',
        element: <LegacyTriageRedirect />,
      },
      // E10.6/E10.7 — pagina "ENISA Reports" (SLA countdown incluso).
      {
        path: 'enisa-reports',
        element: <ReportPage />,
      },
      {
        path: 'enisa-reports/:id',
        element: <SrpThreadDetail />,
      },
      // Route storiche /srp e /srp/:id → /enisa-reports… (bookmark, link già inviati)
      {
        path: 'srp',
        element: <LegacySrpRedirect />,
      },
      {
        path: 'srp/*',
        element: <LegacySrpRedirect />,
      },
      // E10.11 / RF-7-1+RF-12-1 — Admin configurazione (slice 4)
      {
        path: 'admin',
        element: <AdminPage />,
      },
      // Slice A — Anagrafica clienti (Forniture)
      {
        path: 'customers',
        element: <CustomersListPage />,
      },
      {
        path: 'customers/:id',
        element: <CustomerDetailPage />,
      },
      // Portale job Prefect — visibile a tutti i ruoli autenticati
      {
        path: 'jobs',
        element: <PrefectJobsPage />,
      },
      // Batch Edit API — elenco batch + dettaglio in tempo reale
      {
        path: 'batch-edits',
        element: <BatchEditsPage />,
      },
      {
        path: 'batch-edits/:batchId',
        element: <BatchEditDetailPage />,
      },
      // E7.6 — Log Audit (VIEW_AUDIT_LOG)
      {
        path: 'audit',
        element: <AuditLogPage />,
      },
      // Glossario dei termini del dominio (visibile a tutti i ruoli autenticati)
      {
        path: 'glossary',
        element: <GlossarioPage />,
      },
    ],
  },
]);
